import apiClient from './client';
import { API_ENDPOINTS } from './config';
// Import mock service for development
import mockApiService from './mock/mockApiService';
import { transformMemberToApi, transformImagesToDocuments } from '../utils/apiDataTransformer';

// Helper function to determine if we should use mock data
const shouldUseMock = (slug) => {
    return slug === 'doe-family';
};

class ApiService {
    // ==================== WEBSITE AUTHENTICATION ====================
    async getWebsiteToken() {
        try {
            const response = await apiClient.post(API_ENDPOINTS.AUTH.WEBSITE_TOKEN);

            // Store website token
            if (response.data?.data?.token) {
                localStorage.setItem('family_tree_website_token', response.data.data.token);
            }

            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    async registerUser(userData) {
        try {
            const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, userData);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    // ==================== FAMILY TREE (Updated Structure) ====================
    async verifySlug(slug) {
        try {
            // Use mock data only for 'doe-family' slug
            if (shouldUseMock(slug)) {
                return await mockApiService.verifySlug(slug);
            }

            // Verify slug with backend
            const url = API_ENDPOINTS.FAMILY_TREES.GET.replace(':slug', slug);
            const response = await apiClient.get(url);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    async getFamilyTree(slug) {
        try {
            // Use mock data only for 'doe-family' slug
            if (shouldUseMock(slug)) {
                return await mockApiService.getFamilyTree(slug);
            }

            // Get family tree from backend
            const url = API_ENDPOINTS.FAMILY_TREES.GET.replace(':slug', slug);
            const response = await apiClient.get(url);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    async getMember(treeSlug, memberId) {
        try {
            const url = API_ENDPOINTS.FAMILY_TREES.GET_MEMBER
                .replace(':treeSlug', treeSlug)
                .replace(':memberId', memberId);
            const response = await apiClient.get(url);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    async createMember(treeSlug, memberData) {
        try {
            // Transform member data to API format
            const apiData = transformMemberToApi(memberData);

            const url = API_ENDPOINTS.MEMBERS.CREATE.replace(':treeSlug', treeSlug);

            const response = await apiClient.post(url, apiData);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    async updateMember(treeSlug, memberId, data) {
        try {
            // Transform member data to API format
            const apiData = transformMemberToApi(data);

            console.log('Update Member - Original Data:', data);
            console.log('Update Member - Transformed API Data:', apiData);

            const url = API_ENDPOINTS.MEMBERS.UPDATE
                .replace(':treeSlug', treeSlug)
                .replace(':memberId', memberId);
            const response = await apiClient.put(url, apiData);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    async deleteMember(treeSlug, memberId) {
        try {
            const url = API_ENDPOINTS.MEMBERS.DELETE
                .replace(':treeSlug', treeSlug)
                .replace(':memberId', memberId);
            const response = await apiClient.delete(url);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    // ==================== IMAGES ====================
    async addImages(treeSlug, memberId, imagesData) {
        try {
            const url = API_ENDPOINTS.IMAGES.ADD
                .replace(':treeSlug', treeSlug)
                .replace(':memberId', memberId);

            const response = await apiClient.post(url, imagesData);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    // ==================== RELATIONSHIPS ====================
    async createRelationship(treeSlug, relationshipData) {
        try {
            const url = API_ENDPOINTS.RELATIONSHIPS.CREATE.replace(':treeSlug', treeSlug);
            const response = await apiClient.post(url, relationshipData);
            return response;
        } catch (error) {
            throw this._handleError(error);
        }
    }

    // ==================== ERROR HANDLING ====================
    _handleError(error) {
        const message = error.response?.data?.message || error.message || 'An error occurred';
        const statusCode = error.response?.status;

        return {
            message,
            statusCode,
            error,
        };
    }
}

export default new ApiService();
